import React, { useMemo, createContext, forwardRef, useContext } from "react";
import {
    FormControl,
    FormLabel,
    TextField,
    Autocomplete,
    Checkbox,
    Chip,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { VariableSizeList } from "react-window";

const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
    const childrenProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...childrenProps} />;
});

// Virtualized Listbox Component
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const ITEM_SIZE = 48;

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * ITEM_SIZE;
        }
        return itemCount * ITEM_SIZE;
    };

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2}
                    width="100%"
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={() => ITEM_SIZE}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {({ data, index, style }) => {
                        const element = data[index];
                        return React.cloneElement(element, {
                            style: {
                                ...style,
                                top: style.top + 1,
                            },
                        });
                    }}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const MultiSelectDropdown = ({
    label,
    placeholder,
    value = [],
    options = [],
    onChange,
    disabled = false,
    loading = false,
    required = false,
    getOptionLabel = (opt) => (typeof opt === "object" ? opt.label : opt),
    getOptionValue = (opt) => (typeof opt === "object" ? opt.value : opt),
}) => {
    const optionsMap = useMemo(() => {
        const map = new Map();
        options.forEach((opt) => {
            map.set(getOptionValue(opt), opt);
        });
        return map;
    }, [options, getOptionValue]);

    const selectedOptions = useMemo(() => {
        return value.map((val) => optionsMap.get(val) || val);
    }, [value, optionsMap]);

    return (
        <FormControl fullWidth size="small">
            {label && (
                <FormLabel
                    required={required}
                    sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#475569",
                        mb: 0.8,
                    }}
                >
                    {label}
                </FormLabel>
            )}

            <Autocomplete
                color="secondary"
                multiple
                disableCloseOnSelect
                disabled={disabled}
                loading={loading}
                options={options}
                value={selectedOptions}
                ListboxComponent={ListboxComponent}
                popupIcon={
                    <KeyboardArrowDownIcon
                        fontSize="small"
                        sx={{ color: "grey.600" }}
                    />
                }
                getOptionLabel={(option) =>
                    typeof option === "object"
                        ? getOptionLabel(option)
                        : String(option)
                }
                isOptionEqualToValue={(option, val) =>
                    getOptionValue(option) === getOptionValue(val)
                }
                onChange={(event, newValue) => {
                    const selectedValues = newValue.map((item) =>
                        typeof item === "object" ? getOptionValue(item) : item
                    );
                    onChange({ target: { value: selectedValues } });
                }}
                renderOption={(props, option, { selected }) => {
                    const optLabel = getOptionLabel(option);
                    const { key, ...optionProps } = props;

                    return (
                        <li key={key} {...optionProps}>
                            <Checkbox
                                color="secondary"
                                style={{ marginRight: 8 }}
                                checked={selected}
                                size="small"
                            />
                            <Typography variant="body2" noWrap>
                                {optLabel}
                            </Typography>
                        </li>
                    );
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        const displayLabel =
                            typeof option === "object"
                                ? getOptionLabel(option)
                                : option;
                        return (
                            <Chip
                                key={key}
                                label={displayLabel}
                                size="small"
                                {...tagProps}
                                sx={{
                                    backgroundColor: (theme) =>
                                        `${theme.palette.secondary.main} !important`,
                                    color: "#ffffff !important",
                                    borderRadius: "16px !important",
                                    "& .MuiChip-label": {
                                        color: "#ffffff !important",
                                    },
                                    "& .MuiChip-deleteIcon": {
                                        color: "#ffffff !important",
                                        fontSize: "16px",
                                        "&:hover": {
                                            color: "#e0e0e0 !important",
                                        },
                                    },
                                }}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        color="secondary"
                        placeholder={value.length === 0 ? placeholder : ""}
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                )}
            />
        </FormControl>
    );
};

export default MultiSelectDropdown;
